import DOMPurify from 'dompurify';
import {isNull, reverse, takeWhile} from 'lodash';
import React, {ReactNode} from 'react';
import {z as r} from 'zod';

import {Locale} from '../../../../../../provider/locale/locale';
import {SnackbarArgsType, SnackbarType} from '../../../../../../provider/snackbar/snackbar-type';
import {ReviewsReplyError} from '../../../../../../service/reviews/reviews-reply';
import {TagGroupType} from '../../../../../../service/reviews/reviews-tags';
import {HttpError} from '../../../../../../util/error';
import {getFromLocalStorage} from '../../../../../../util/local-storage';
import {isRecord} from '../../../../../../util/object';
import {URL_REGEXP} from '../../../../../../util/regexp';
import {toTrimmedString} from '../../../../../../util/string';
import {IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY} from '../../../reviews-header/reviews-header-const';

import {ReviewLazyImage} from './lazy-image/review-lazy-image';
import {PreviewGroup} from './preview-group/preview-group';
import * as styles from './new-review.scss';

// we get the string with two <img> tags here, first with the base64 src and second with the http link to source image
function renderLazyImage(imageTags: string) {
    const [base64, http] = new DOMParser().parseFromString(imageTags, 'text/html').querySelectorAll('img');
    const image = http || base64;

    if (!image) {
        return null;
    }

    return <ReviewLazyImage alt={image.alt} src={image.src} />;
}

function getTrailingImgTags(data: Array<string>): {
    images: Array<{src: string | null; alt: string | null}>;
} {
    const trailingImgs = reverse(takeWhile([...data].reverse(), (item) => item.trim().startsWith('<img')));

    const parsedImgs = trailingImgs.map((tag) => {
        const srcMatch = tag.match(/src=["']([^"']+)["']/);
        const altMatch = tag.match(/alt=["']([^"']+)["']/);

        return {
            src: srcMatch && srcMatch[1] ? srcMatch[1] : null,
            alt: altMatch && altMatch[1] ? altMatch[1] : null,
        };
    });

    return {
        images: parsedImgs,
    };
}

export function constructComment(text: string, withLinks?: boolean): Array<JSX.Element> {
    const trimmedText = toTrimmedString(text);

    const {images} = getTrailingImgTags(trimmedText.split('\n').filter((imgText) => imgText !== ''));

    const photoGallery = <PreviewGroup imageClassName={styles.ReviewGallery_image} images={images} />;

    if (trimmedText === '') {
        return [];
    }

    // server processes all the comment text on its side, so that
    // we can safely split by \n and map over each line
    const trimmedTextArray = trimmedText
        .split('\n')
        .filter((filteredDText) => !images.some((image) => (image.src ? filteredDText.includes(image.src) : false)))
        .map((part: string, index: number): JSX.Element => {
            let cleanedText = part === '' ? '<br/>' : DOMPurify.sanitize(part);
            const hasImage = cleanedText.includes('<img');

            if (withLinks) {
                cleanedText = cleanedText
                    .split(' ')
                    .map((substring) => substring.replace(URL_REGEXP, (url) => `<a href="${url}">${url}</a>`))
                    .join(' ');
            }

            cleanedText = cleanedText.replaceAll('<a', '<a target="_blank"');

            if (hasImage) {
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={String(index)}>{renderLazyImage(cleanedText)}</div>
                );
            }

            return (
                // eslint-disable-next-line react/no-danger, react/no-array-index-key
                <div dangerouslySetInnerHTML={{__html: cleanedText.trim()}} key={String(index)} />
            );
        });

    return [...trimmedTextArray, photoGallery];
}

function constructCommentDocument(text: string): Document {
    const trimmedText = toTrimmedString(text);
    const cleanedText = DOMPurify.sanitize(trimmedText);

    return new DOMParser().parseFromString(cleanedText, 'text/html');
}

function getImagesFromCommentDocument(commentDocument: Document): NodeListOf<HTMLImageElement> {
    return commentDocument.querySelectorAll('img');
}

export function extractReviewCommentContent(comment: string | null): {
    images: Array<HTMLImageElement>;
    textWithImages: Array<React.JSX.Element>;
} | null {
    if (!comment) {
        return null;
    }

    const commentDocument = constructCommentDocument(comment);

    const textWithImages = constructComment(comment);
    const images = [...getImagesFromCommentDocument(commentDocument)];

    return {images, textWithImages};
}

export function constructCommentString(text: string): string {
    const commentDocument = constructCommentDocument(text);
    const initialText: string = commentDocument.documentElement.textContent || '';

    return initialText.replace(/(\n){3,}/g, '\n\n'); // two \n (new lines) in arrow is maximum
}

export function constructCommentPreview(text: string, limit = 2500): string {
    const commentString: string = constructCommentString(text);

    if (commentString.length > limit) {
        return `${commentString.slice(0, limit)}…`;
    }

    return commentString;
}

export function handleReviewReplyExceptions(
    error: unknown,
    snackbar: SnackbarType,
    snackbarArgs?: SnackbarArgsType,
    handle423Error?: () => void
): void {
    if (error instanceof ReviewsReplyError) {
        if (error.statusCode === 423 && handle423Error) {
            handle423Error();
        }

        // eslint-disable-next-line react/jsx-no-useless-fragment
        snackbar.error(<>{error.jsonData.error || error.jsonData.detail}</>);
        return;
    }

    snackbar.error(
        snackbarArgs ?? {
            description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
            message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
        }
    );

    if (!(error instanceof HttpError)) {
        // This is not HttpError|ApiError. This error is in js code. A developer should see it in a console
        console.error(error);
    }
}

export function newHandleEnter(
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    submitHandler: (() => Promise<void>) | null
): void {
    const localStorageVariantSendReply = getFromLocalStorage(
        IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY,
        r.boolean(),
        false
    );

    if (localStorageVariantSendReply) {
        if (!event.shiftKey) {
            event.preventDefault();
            submitHandler?.();
        }
    } else if (event.shiftKey) {
        event.preventDefault();
        submitHandler?.();
    }
}

export function checkHasJsonData(error: unknown): error is {jsonData: ReactNode} {
    return isRecord(error) && Reflect.has(error, 'jsonData');
}

const emojiRegex =
    /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}(?:\p{Emoji_Modifier})?|\p{Emoji_Component})+$/u;

export function isOnlyEmojis(text: string | null): boolean {
    if (isNull(text)) {
        return false;
    }

    return emojiRegex.test(text);
}

export function getMissingRequiredGroups(
    groups: Array<TagGroupType>,
    reviewGroupIds: Array<number>
): Array<TagGroupType> {
    const requiredGroups = groups.filter((group) => group.isRequired);

    const reviewIdSet = new Set(reviewGroupIds);

    const hasIntersection = requiredGroups.some((group) => reviewIdSet.has(group.id));

    if (!hasIntersection) {
        return requiredGroups;
    }

    return requiredGroups.filter((group) => !reviewIdSet.has(group.id));
}

async function processBatchRecursive<T>(
    items: Array<T>,
    batchSize: number,
    processor: (item: T) => Promise<unknown>,
    startIndex: number
): Promise<void> {
    if (startIndex >= items.length) {
        return;
    }

    const batch = items.slice(startIndex, startIndex + batchSize);

    await Promise.allSettled(
        batch.map((item) =>
            processor(item).catch((error) => {
                console.error('Error processing item:', error);
            })
        )
    );

    // Small delay between batches to avoid overwhelming the server
    if (startIndex + batchSize < items.length) {
        await new Promise((resolve) => {
            setTimeout(resolve, 50);
        });

        await processBatchRecursive(items, batchSize, processor, startIndex + batchSize);
    }
}

export async function processBatch<T>(
    items: Array<T>,
    batchSize: number,
    processor: (item: T) => Promise<unknown>
): Promise<void> {
    await processBatchRecursive(items, batchSize, processor, 0);
}
